import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PersonSimpleWalk } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function StretchingPublic() {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/stretch/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ routines: [] }));
  }, []);

  const today = board?.routine_of_the_day;

  useSeo({
    title: "Stretching that works — guided routines and range of motion | Unity & Utility",
    description: "Short guided stretch routines for drivers, warehouse and workshop work, with a range-of-motion self-check, a score over time and bonus points for keeping it up.",
    path: "/stretching",
    image: today?.image_url ? `${BACKEND}${today.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Stretching", "/stretching"]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="stretching-public-page">
        <Crumbs items={[["Home", "/"], ["Stretching", "/stretching"]]} />
        <p className="overline text-[#B3241B]">Easy, effective, every day</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <PersonSimpleWalk size={34} weight="duotone" className="mt-1 shrink-0 text-[#002FA7]" />
          Stretching &amp; range of motion
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="stretching-public-intro">
          Short guided routines for the work people actually do — long drives, lifting, standing and
          tool work. Follow the timer, rate your range of motion as you go, and collect points for
          every routine you finish. A self-check, never a diagnosis.
        </p>
        <div className="mt-5">
          <ShareBar
            url={board?.share_token ? `${window.location.origin}/p/${board.share_token}` : `${window.location.origin}/stretching`}
            text={today ? `Stretch of the day — ${today.name}` : "Stretching and range of motion at work"}
            testid="stretching-public-share" instagram />
        </div>

        {today && (
          <section className="mt-10 border border-[#002FA7] bg-white" data-testid="stretching-today">
            {today.image_url && (
              <img src={`${BACKEND}${today.image_url}`} alt={today.name} loading="lazy"
                className="h-56 w-full object-cover sm:h-72" />
            )}
            <div className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="overline text-[#002FA7]">Stretch of the day{board.day ? ` · ${board.day}` : ""}</p>
                {board.auto_posted && (
                  <span data-testid="stretching-posted" className="overline text-[0.6rem] text-[#0B5C2E]">Posted to Facebook</span>
                )}
              </div>
              <h2 className="font-display mt-1 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
                {today.name}
              </h2>
              <p className="text-sm text-slate-600">{today.minutes} min · {today.where}</p>
              <ol className="mt-3 space-y-1 text-sm text-slate-700" data-testid="stretching-today-steps">
                {(today.steps || []).map((s, i) => (
                  <li key={i}>{i + 1}. {s.move} — {s.seconds}s <span className="text-slate-500">{s.how}</span></li>
                ))}
              </ol>
            </div>
          </section>
        )}

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="stretching-routines">
          {(board?.routines || []).map((r) => (
            <article key={r.id} data-testid={`stretching-routine-${r.id}`} className="border border-border bg-white">
              {r.image_url && (
                <img src={`${BACKEND}${r.image_url}`} alt={r.name} loading="lazy" className="h-36 w-full object-cover" />
              )}
              <div className="p-4">
                <p className="text-sm font-semibold text-[#0F172A]">{r.name}</p>
                <p className="text-xs text-slate-500">{r.minutes} min · {r.where}</p>
                <ul className="mt-2 space-y-0.5 text-sm text-slate-700">
                  {(r.steps || []).slice(0, 4).map((s, i) => <li key={i}>· {s.move} — {s.seconds}s</li>)}
                </ul>
                <div className="mt-3">
                  <ShareBar url={`${window.location.origin}/stretching`} text={`${r.name} — ${r.minutes} minute stretch routine`}
                    testid={`stretching-share-${r.id}`} compact instagram />
                </div>
              </div>
            </article>
          ))}
        </div>

        {board?.points && (
          <p className="mt-8 text-sm text-slate-600" data-testid="stretching-points">
            Points: {board.points.routine} for a finished routine, {board.points.check} for a
            range-of-motion check, {board.points.streak_bonus} bonus for five days running and
            {" "}{board.points.month_bonus} for twenty routines in a month.
          </p>
        )}

        <Link to="/wellbeing" data-testid="stretching-to-wellbeing"
          className="tap-safe mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
          See breaks, naps and the therapy plans
        </Link>
      </div>
    </PublicShell>
  );
}
